<template>
  <div class="pagination-bar">
    <ul class="pagination mb-0">
      <li
        class="page-item"
        :class="{ disabled: currentPage === 1 }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(currentPage - 1)"
          :aria-label="t('aria.previousPage')"
        >
          <span aria-hidden="true">&laquo;</span>
        </a>
      </li>
      <li
        class="page-item"
        v-if="showStartPage"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(1)"
          >1</a
        >
      </li>
      <li
        class="page-item"
        v-if="showStartEllipsis"
        @click="gotoPage(currentPage - 5 < 1 ? 1 : currentPage - 5)"
      >
        <a
          class="page-link"
          href="#"
          >...</a
        >
      </li>
      <li
        class="page-item"
        v-for="page in visiblePages"
        :key="page"
        :class="{ active: currentPage === page }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(page)"
          >{{ page }}</a
        >
      </li>
      <li
        class="page-item"
        v-if="showEndEllipsis"
        @click="gotoPage(currentPage + 5 > totalPages ? totalPages : currentPage + 5)"
      >
        <a
          class="page-link"
          href="#"
          >...</a
        >
      </li>
      <li
        class="page-item"
        v-if="showEndPage"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(totalPages)"
          >{{ totalPages }}</a
        >
      </li>
      <li
        class="page-item"
        :class="{ disabled: currentPage === totalPages }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="gotoPage(currentPage + 1)"
          :aria-label="t('aria.nextPage')"
        >
          <span aria-hidden="true">&raquo;</span>
        </a>
      </li>
    </ul>

    <div class="pagination-bar__nav">
      <div
        ref="onPageSelectRef"
        class="custom-select-wrap drop-up"
        :class="{ open: onPageMenuOpen }"
      >
        <button
          type="button"
          class="custom-select-trigger"
          :aria-expanded="onPageMenuOpen"
          aria-haspopup="listbox"
          :aria-label="t('aria.onPage')"
          @click="onPageMenuOpen = !onPageMenuOpen"
        >
          <span>{{ onPage }}</span>
          <i
            class="bi bi-chevron-down"
            aria-hidden="true"
          />
        </button>
        <div
          class="custom-select-menu"
          role="listbox"
        >
          <button
            v-for="option in onPageArr"
            :key="option"
            type="button"
            class="custom-select-option"
            :class="{ active: Number(option) === Number(onPage) }"
            role="option"
            :aria-selected="Number(option) === Number(onPage)"
            @click="selectOnPage(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>

      <div
        class="input-group w-auto"
        v-if="totalPages > 2"
      >
        <input
          type="number"
          class="form-control"
          v-model="goToPageNumber"
          min="1"
          :max="totalPages"
          @keydown.enter.prevent="gotoPage(goToPageNumber)"
        />
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary rounded-end-3"
          @click="gotoPage(goToPageNumber)"
          :disabled="goToPageNumber < 1 || goToPageNumber > totalPages"
        >
          <i class="bi bi-chevron-double-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { isNil } from 'lodash-es'

const { t } = useI18n()
const emit = defineEmits(['pageChange'])

const DEFAULT_ONPAGE = 20
const props = defineProps({
  totalItem: {
    type: Number,
    default: 1,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  visiblePageCount: {
    type: Number,
    default: 5,
  },
  onPageArr: {
    type: Array,
    default: () => [5, 10, 20, 50],
  },
  onPage: {
    type: Number,
    default: 20,
  },
})

const totalPagesView = ref([])
const goToPageNumber = ref('')
const onPageMenuOpen = ref(false)
const onPageSelectRef = ref(null)

onClickOutside(onPageSelectRef, () => {
  onPageMenuOpen.value = false
})

const gotoPage = (page, _onPage) => {
  let onPage = _onPage
  if (isNil(onPage)) {
    onPage = props.onPage
  }
  if (isNil(onPage)) {
    onPage = DEFAULT_ONPAGE
  }
  emit('pageChange', { page, onPage })
}

const totalPages = computed(() => {
  return Math.ceil(props.totalItem / props.onPage)
})

const startIndex = computed(() => {
  let start = props.currentPage - Math.ceil(props.visiblePageCount / 2)
  if (start < 0) start = 0
  else if (start > totalPagesView.value.length - props.visiblePageCount)
    start = totalPagesView.value.length - props.visiblePageCount
  return start
})

const endIndex = computed(() => {
  return Math.min(startIndex.value + props.visiblePageCount, totalPagesView.value.length)
})

const visiblePages = computed(() => {
  if (totalPagesView.value.length <= props.visiblePageCount) {
    return totalPagesView.value.length ? totalPagesView.value : [1]
  }
  return totalPagesView.value.slice(startIndex.value, endIndex.value)
})

const showStartPage = computed(() => {
  return startIndex.value > 0
})

const showStartEllipsis = computed(() => {
  return startIndex.value > 1
})

const showEndEllipsis = computed(() => {
  return endIndex.value < totalPagesView.value.length - 1
})

const showEndPage = computed(() => {
  return endIndex.value < totalPagesView.value.length
})

watch(
  totalPages,
  newVal => {
    const pages = []
    for (let i = 1; i <= newVal; i++) {
      pages.push(i)
    }
    totalPagesView.value = pages
  },
  { immediate: true }
)

/**
 * Смена числа строк на странице.
 *
 * @param {number|string} option выбранное значение
 * @returns {void}
 */
function selectOnPage(option) {
  onPageMenuOpen.value = false
  const next = Number(option)
  if (Number(props.onPage) === next) {
    return
  }
  gotoPage(1, next)
}
</script>

<i18n>
{
  "ru": {
    "aria": {
      "previousPage": "Предыдущая страница",
      "nextPage": "Следующая страница",
      "onPage": "Записей на странице"
    }
  },
  "en": {
    "aria": {
      "previousPage": "Previous page",
      "nextPage": "Next page",
      "onPage": "Rows per page"
    }
  }
}
</i18n>
